import { Alert, AlertColor, Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useIsExpertOrGreater } from "../../data/Results/useIsExpertOrGreater";
import { useResultMeasurementStatusComments } from "../../data/Results/useResultMeasurementStatusComments";
import { Card } from "../../shared/Card";
import { Measurement, MeasurementDetail, Result } from "../../shared/interfaces/Result";
import ValueText from "../../shared/LabelValueFormatters/ValueText";
import { Snackbar } from "../../shared/Snackbar";
import { MeasurementNewCommentDialog } from "../MeasurementNewCommentDialog";
import RAGStatusChip from "../RAGStatusChip";

interface MeasurementCommentsCardProps {
  measurementDetail: MeasurementDetail<Measurement>;
  captureId: number;
  selectedResult?: Result;
}

export function MeasurementCommentsCard(props: MeasurementCommentsCardProps) {
  const { t } = useTranslation();
  const { measurementDetail, captureId, selectedResult } = props;

  const { customerId } = useParams<{ customerId: string }>();

  const substationName =
    selectedResult?.dataValue.SubstationSummary?.SubstationName ??
    selectedResult?.dataValue.MetaData?.SubstationGroup?.SubstationName;
  const isExpertOrGreater = useIsExpertOrGreater(
    Number(customerId),
    selectedResult?.dataValue.SubstationSummary?.SubstationId,
    substationName,
  );

  const [newCommentDialogOpen, setNewCommentDialogOpen] = React.useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const comments = useResultMeasurementStatusComments(
    captureId,
    Number(customerId),
    measurementDetail.panel360AssetId,
    measurementDetail.panelNo,
    measurementDetail.measurement,
  );

  function handleAddCommentClick() {
    setNewCommentDialogOpen(true);
  }

  function handleAddCommentSuccess(successMessage: string) {
    setSnackbarSeverity("success");
    comments.refetch();
    setSnackbarText(successMessage);
    setShowSnackbar(true);
  }

  function handleAddCommentClosed() {
    setNewCommentDialogOpen(false);
  }

  function handleAddCommentError(errorMessage: string) {
    setSnackbarSeverity("error");
    setSnackbarText(errorMessage);
    setShowSnackbar(true);
  }

  const handleSnackbarClose = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  return (
    <>
      <MeasurementNewCommentDialog
        open={newCommentDialogOpen}
        measurementDetail={measurementDetail}
        captureId={captureId}
        onSuccess={handleAddCommentSuccess}
        onError={handleAddCommentError}
        onClose={handleAddCommentClosed}
      />

      <Card
        title={t("common.comments")}
        action={
          isExpertOrGreater.data?.valueOf() && (
            <Button variant="contained" color="secondary" onClick={handleAddCommentClick}>
              {t("common.addComment")}
            </Button>
          )
        }
      >
        <Stack spacing={2} divider={<Divider />}>
          {comments.data && comments.data?.length > 0 ? (
            comments.data?.map((comment, index) => (
              <Grid key={index} container>
                <Grid item xs={10} display="flex" alignItems="center">
                  <Stack flex={1} alignItems="flex-start">
                    <Typography variant="caption" color="textSecondary">
                      {t("common.resultsCommentCaption", {
                        username: comment.username,
                        dateString: comment.created,
                      })}
                    </Typography>
                    {
                      <>
                        {
                          // If the new and old RAG status are the same then the user has added a comment without updating the status so do not show the updated and previous RAG status.
                          comment.newUtpRAGStatus !== comment.oldUtpRAGStatus && (
                            <ValueText>
                              Updated: {<RAGStatusChip ragStatus={comment.newUtpRAGStatus} />} Previous:{" "}
                              {<RAGStatusChip ragStatus={comment.oldUtpRAGStatus} />}
                            </ValueText>
                          )
                        }
                        <ValueText>{comment.comment}</ValueText>
                      </>
                    }
                  </Stack>
                </Grid>
              </Grid>
            ))
          ) : (
            <Box display="flex" justifyContent="center">
              <Typography variant="subtitle2" color="textSecondary">
                {t("common.commentsEmpty")}
              </Typography>
            </Box>
          )}
        </Stack>
      </Card>

      <Snackbar open={showSnackbar} onClose={handleSnackbarClose}>
        <Alert severity={snackbarSeverity} variant="filled">
          {snackbarText}
        </Alert>
      </Snackbar>
    </>
  );
}
